import { useQueryClient } from '@tanstack/react-query';
import { T } from '@tolgee/react';
import styled from 'styled-components';

import theme from '../../../../../assets/theme';
import { presentationsCacheKey } from '../../../../../hooks/usePresentations';
import useRouterMatch from '../../../../../hooks/useRouterMatch';
import { templatesCacheKey } from '../../../../../hooks/useTemplates';
import { Button } from '../../../../_atoms/Button';
import BaseModal from '../../../../_molecules/BaseModal';

/**
 * Popup shown on top of a generator form when a lesson-generation request fails.
 * The copy depends on *why* it failed (CURI-362):
 *
 * - `isConnectionError` (no HTTP response — a dropped connection / timeout): the
 *   request usually still reached the backend, which finishes generating, so we
 *   reassure the user and point them to My Lessons (with a "Go to My Lessons"
 *   action that refetches the list).
 * - otherwise (the server responded with an error — 4xx/5xx): nothing was
 *   created, so we do NOT promise a lesson in My Lessons — just offer a retry.
 *
 * Rendered once by GenerationErrorProvider; the generation hooks drive it off
 * their `isError`, so the data hooks stay UI-free.
 */
export const GenerationErrorModal = ({
  open,
  isConnectionError,
  onRetry,
  onClose,
}: {
  open: boolean;
  isConnectionError: boolean;
  onRetry: () => void;
  onClose: () => void;
}) => {
  const { router, tenantId } = useRouterMatch();
  const client = useQueryClient();

  const goToMyLessons = () => {
    // On a dropped connection the lesson usually still finished on the backend,
    // but our caches hold the pre-generation blank: the My Lessons list
    // (`presentations`, staleTime 1h) and the per-lesson content
    // (`['template', id]`, seeded from that list) would otherwise render empty
    // until they expire. Invalidate both so opening the lesson refetches its
    // real content from the DB. See CURI-362.
    client.invalidateQueries({ queryKey: [presentationsCacheKey] });
    client.invalidateQueries({ queryKey: [templatesCacheKey] });
    client.invalidateQueries({ queryKey: ['template'] });
    // Dismiss the popup — it lives in an app-wide provider, so without this it
    // would survive the client-side navigation and linger on My Lessons.
    onClose();
    router.push(`/${tenantId}/lessons`);
  };

  return (
    <BaseModal
      open={open}
      onCancel={onClose}
      width="440px"
      title={
        isConnectionError ? (
          <T keyName="Hang tight" defaultValue="Hang tight" />
        ) : (
          <T keyName="Something went wrong" defaultValue="Something went wrong" />
        )
      }
    >
      <Body>
        {isConnectionError ? (
          <T
            keyName="Your Wi-Fi connection dropped, but your lesson is still being created. You'll find it in My Lessons in a few minutes."
            defaultValue="Your Wi-Fi connection dropped, but your lesson is still being created. You'll find it in My Lessons in a few minutes."
          />
        ) : (
          <T
            keyName="We couldn't create your lesson this time. Please try again."
            defaultValue="We couldn't create your lesson this time. Please try again."
          />
        )}
      </Body>
      <Actions>
        <Button type="orange" width="100%" onClick={onRetry}>
          <T keyName="Try again" defaultValue="Try again" />
        </Button>
        {isConnectionError && (
          <Button type="ghost" width="100%" onClick={goToMyLessons}>
            <T keyName="Go to My Lessons" defaultValue="Go to My Lessons" />
          </Button>
        )}
      </Actions>
    </BaseModal>
  );
};

const Body = styled.p`
  text-align: center;
  font-size: 16px;
  color: ${theme.colors.black};
  margin: 0 0 24px;
  max-width: 360px;
`;

const Actions = styled.div`
  display: flex;
  gap: 12px;
  width: 100%;

  > * {
    flex: 1 1 0;
    min-width: 0;
  }

  @media (max-width: 480px) {
    flex-direction: column;
  }
`;
